import React, { useState, useEffect, useRef } from 'react';
import { Camera, UploadCloud, CheckCircle2, Trash2, Eye, FileCheck, Crop, Sparkles } from 'lucide-react';
import DocumentScannerModal from './DocumentScannerModal';

interface DeclaracionJuradaUploadProps {
    setFile?: (file: File | null) => void;
    onFileSelected?: (file: File | null) => void;
    currentImageUrl?: string | null;
    fotoActualUrl?: string | null;
    isSigned?: boolean;
    yaFirmada?: boolean;
    fechaFirma?: string | null;
}

const DeclaracionJuradaUpload: React.FC<DeclaracionJuradaUploadProps> = ({
    setFile,
    onFileSelected,
    currentImageUrl,
    fotoActualUrl,
    isSigned = false,
    yaFirmada = false,
    fechaFirma,
}) => {
    const activeCurrentUrl = currentImageUrl || fotoActualUrl || null;
    const activeIsSigned = isSigned || yaFirmada;

    const [previewUrl, setPreviewUrl] = useState<string | null>(activeCurrentUrl);
    const [fileName, setFileName] = useState<string | null>(null);
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [scannerInitialImage, setScannerInitialImage] = useState<string | File | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (activeCurrentUrl) {
            setPreviewUrl(activeCurrentUrl);
        }
    }, [activeCurrentUrl]);

    const notifyFileChange = (file: File | null) => {
        if (setFile) setFile(file);
        if (onFileSelected) onFileSelected(file);
    };

    const handleFileSelected = (file: File | null) => {
        if (file) {
            notifyFileChange(file);
            setFileName(file.name);
            const objectUrl = URL.createObjectURL(file);
            setPreviewUrl(objectUrl);
        } else {
            notifyFileChange(null);
            setFileName(null);
            setPreviewUrl(activeCurrentUrl || null);
        }
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        notifyFileChange(null);
        setFileName(null);
        setPreviewUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleOpenScanner = () => {
        setScannerInitialImage(null);
        setIsScannerOpen(true);
    };

    const handleOpenEditorForCurrent = () => {
        if (previewUrl) {
            setScannerInitialImage(previewUrl);
            setIsScannerOpen(true);
        }
    };

    const handleScannerSave = (file: File) => {
        handleFileSelected(file);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <FileCheck className="h-4 w-4 text-[#61257b]" />
                    Declaración Jurada y Firma
                </label>
                {activeIsSigned && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="h-3 w-3" /> Firmada
                        {fechaFirma && ` (${new Date(fechaFirma).toLocaleDateString('es-ES')})`}
                    </span>
                )}
            </div>

            {/* Upload / Preview Card */}
            <div className="border-2 border-dashed border-slate-200 hover:border-[#61257b]/40 rounded-xl p-4 transition-all bg-slate-50/50 hover:bg-slate-50">
                {previewUrl ? (
                    <div className="relative group">
                        <div className="flex items-center gap-4 bg-white p-3 rounded-lg border border-slate-200 shadow-xs">
                            <div className="h-20 w-28 bg-slate-100 rounded-md overflow-hidden flex-shrink-0 border border-slate-200 relative group/img">
                                <img
                                    src={previewUrl}
                                    alt="Foto de la firma"
                                    className="h-full w-full object-cover"
                                />
                                <button
                                    type="button"
                                    onClick={handleOpenEditorForCurrent}
                                    title="Editar / Recortar Hoja"
                                    className="absolute inset-0 bg-black/50 text-white opacity-0 group-hover/img:opacity-100 flex items-center justify-center gap-1 text-[10px] font-bold transition-opacity"
                                >
                                    <Crop className="h-3.5 w-3.5" /> Recortar
                                </button>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-slate-800 truncate">
                                    {fileName || 'Foto de declaración jurada'}
                                </p>
                                <p className="text-[11px] text-emerald-600 font-semibold flex items-center gap-1 mt-0.5">
                                    <CheckCircle2 className="h-3 w-3" /> Imagen lista y optimizada
                                </p>
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={handleOpenScanner}
                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-[#61257b] hover:underline"
                                    >
                                        <Camera className="h-3 w-3" /> Escanear otra foto
                                    </button>
                                    <span className="text-slate-300">|</span>
                                    <button
                                        type="button"
                                        onClick={handleOpenEditorForCurrent}
                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-[#c1348b] hover:underline"
                                    >
                                        <Crop className="h-3 w-3" /> Reajustar recorte
                                    </button>
                                    <span className="text-slate-300">|</span>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 hover:underline"
                                    >
                                        <UploadCloud className="h-3 w-3" /> Cambiar archivo
                                    </button>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleRemove}
                                className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                title="Quitar imagen"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <div className="flex justify-center gap-3 mb-3">
                            {/* Botón principal: Escáner con Cámara Inteligente */}
                            <button
                                type="button"
                                onClick={handleOpenScanner}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#61257b] to-[#c1348b] hover:from-[#511e67] hover:to-[#a92b78] text-white text-xs font-bold shadow-md hover:shadow-lg transition-all cursor-pointer"
                            >
                                <Camera className="h-4 w-4" />
                                Tomar Foto a Firma (Escáner)
                            </button>

                            {/* Subir archivo */}
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 text-xs font-bold hover:bg-[#61257b]/5 hover:border-[#61257b]/30 transition-all shadow-2xs cursor-pointer"
                            >
                                <UploadCloud className="h-4 w-4 text-[#61257b]" />
                                Subir Archivo
                            </button>
                        </div>
                        <p className="text-[11px] text-slate-500">
                            Abre la cámara para encuadrar, recortar y mejorar la hoja automáticamente
                        </p>
                    </div>
                )}

                {/* Input archivo oculto */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        if (file) {
                            // Abrir en el editor de recorte para que el usuario pueda encuadrar y optimizar
                            setScannerInitialImage(file);
                            setIsScannerOpen(true);
                        }
                    }}
                />
            </div>

            {/* Modal de Escáner Inteligente con Cámara y Recorte */}
            {isScannerOpen && (
                <DocumentScannerModal
                    isOpen={isScannerOpen}
                    onClose={() => setIsScannerOpen(false)}
                    onSave={handleScannerSave}
                    initialImage={scannerInitialImage}
                />
            )}
        </div>
    );
};

export default DeclaracionJuradaUpload;
